import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useRef,
} from "react";
import {
  PrivyProvider,
  usePrivy,
  User as PrivyUser,
} from "@privy-io/react-auth";
import { AlchemyProvider } from "@account-kit/privy-integration";
import { useNavigate } from "react-router-dom";
import sdk from "@farcaster/miniapp-sdk";
import {
  PRIVY_APP_ID,
  ALCHEMY_API_KEY,
  ALCHEMY_GAS_POLICY_ID,
} from "@/utils/constants";
import { authApi } from "@/api/auth";
import { AppUrlListener } from "@/components/AppUrlListener";
import { ErrorModal } from "@/components/ErrorModal";
import { refineErrorMessage, getErrorTitle } from "@/utils/errorMessages";
import { setSentryUser, clearSentryUser, Sentry } from "@/config/sentry";
import posthog from "posthog-js";
import { analytics } from "@/services/analytics";

interface User {
  id: string;
  email: string;
  username: string | null;
  name?: string | null;
  avatarUrl?: string | null;
  walletAddress?: string | null;
}

interface AuthContextType {
  user: User | null;
  privyUser: PrivyUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  isTokenReady: boolean; // True only when auth token is confirmed available
  login: () => void;
  logout: () => void;
  updateUser: (updatedUser: User) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Inner component that uses Privy hooks
function AuthProviderInner({ children }: { children: React.ReactNode }) {
  const {
    user: privyUser,
    authenticated,
    ready,
    login: privyLogin,
    logout: privyLogout,
    getAccessToken,
  } = usePrivy();

  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isTokenReady, setIsTokenReady] = useState(false); // Gate for authenticated API calls
  const [hasRedirectedToProfile, setHasRedirectedToProfile] = useState(false);
  const hasSyncedWithBackend = useRef(false);
  const hasAttemptedJwtRestore = useRef(false);
  // Track if this is a fresh login (user clicked login) vs session restoration (page reload)
  const isFreshLogin = useRef(false);

  // Track when we started waiting for wallet (for timeout)
  const walletWaitStartTime = useRef<number | null>(null);
  const WALLET_TIMEOUT_MS = 15000; // 15 seconds timeout

  // Helper to store token and verify it's readable (handles iOS Safari timing issues)
  const storeAndVerifyToken = (token: string): boolean => {
    localStorage.setItem("authToken", token);
    // Immediately verify the token was stored (iOS Safari can have timing issues)
    const storedToken = localStorage.getItem("authToken");
    return storedToken === token;
  };

  // Error modal state
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorTitle, setErrorTitle] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  // Try to restore user from existing JWT on app load (handles iOS Safari session loss)
  //
  // IMPORTANT: This effect handles a specific edge case where iOS Safari clears Privy's
  // cookies but our JWT in localStorage is still valid. In this case, Privy shows as
  // "not authenticated" but we can restore the session from our JWT.
  //
  // RACE CONDITION FIX: We must NOT set isLoading=false here if Privy IS authenticated,
  // because that would cause a flash of the landing page before syncUser completes.
  // When Privy is authenticated, syncUser will set isLoading=false after it completes.
  useEffect(() => {
    const restoreFromJwt = async () => {
      if (hasAttemptedJwtRestore.current) return;
      hasAttemptedJwtRestore.current = true;

      const existingToken = localStorage.getItem("authToken");

      // No token exists - if Privy is also not authenticated, we're done loading
      // If Privy IS authenticated, syncUser will handle setting isLoading=false
      if (!existingToken) {
        if (!authenticated) {
          setIsLoading(false);
        }
        return;
      }

      // If Privy is ready but not authenticated, try to restore from JWT
      // This handles the case where iOS Safari cleared Privy's cookies but our JWT is still valid
      if (ready && !authenticated && !user) {
        console.log("[Auth] Privy session lost, attempting JWT restore...");
        try {
          const profile = await authApi.getProfile();
          if (profile.user) {
            console.log("[Auth] Successfully restored user from JWT");
            // Token already exists and is valid - mark as ready BEFORE setting user
            setIsTokenReady(true);
            setUser(profile.user);
            setSentryUser({
              id: profile.user.id,
              email: profile.user.email,
              walletAddress: profile.user.walletAddress || undefined,
            });
            // Identify user in PostHog for analytics
            posthog.identify(profile.user.id, {
              email: profile.user.email,
              username: profile.user.username,
              walletAddress: profile.user.walletAddress,
            });
            hasSyncedWithBackend.current = true;
          }
          // JWT restore complete - safe to stop loading
          setIsLoading(false);
        } catch (error) {
          // JWT is invalid or expired, clear it
          console.log("[Auth] JWT restore failed, clearing token");
          Sentry.captureException(error, {
            tags: { auth_flow: 'jwt_restore' },
            extra: { hasToken: !!existingToken },
          });
          localStorage.removeItem("authToken");
          setIsTokenReady(false);
          // JWT restore failed - safe to stop loading (will show landing page)
          setIsLoading(false);
        }
      }
      // NOTE: If Privy IS authenticated (authenticated=true), we do NOT set isLoading=false here.
      // The syncUser effect will handle that after it completes the backend sync.
      // This prevents the landing page from flashing before the user data is loaded.
    };

    if (ready) {
      restoreFromJwt();
    }
  }, [ready, authenticated, user]);

  // Sync with backend when Privy user changes
  useEffect(() => {
    const syncUser = async () => {
      if (authenticated && privyUser && !hasSyncedWithBackend.current) {
        // Check for wallet - Privy creates embedded wallet asynchronously after login
        const walletAddress =
          privyUser.wallet?.address ||
          privyUser.linkedAccounts?.find((acc) => acc.type === "wallet")
            ?.address;

        if (!walletAddress) {
          // Start timeout timer if not already started
          if (walletWaitStartTime.current === null) {
            walletWaitStartTime.current = Date.now();
            console.log("[Auth] Waiting for embedded wallet to be created...");
          }

          // Check if we've exceeded timeout
          const waitTime = Date.now() - walletWaitStartTime.current;
          if (waitTime > WALLET_TIMEOUT_MS) {
            console.error("[Auth] Wallet creation timed out after", waitTime, "ms");

            // Track wallet timeout in Sentry
            Sentry.captureMessage("Wallet creation timed out", {
              level: "error",
              tags: { auth_flow: 'wallet_creation' },
              extra: {
                waitTime,
                timeoutMs: WALLET_TIMEOUT_MS,
                privyUserId: privyUser?.id,
                email: privyUser?.email?.address,
              },
            });

            walletWaitStartTime.current = null; // Reset for next attempt

            // Force logout and show error
            await privyLogout();
            setUser(null);
            localStorage.removeItem("authToken");

            setErrorTitle("Wallet Creation Failed");
            setErrorMessage(
              "Your wallet couldn't be created in time. Please try logging in again. If the problem persists, contact support."
            );
            setShowErrorModal(true);
            setIsLoading(false);
            return;
          }

          // Wallet not ready yet - exit and wait for next privyUser update
          // The useEffect will re-run when privyUser changes
          return;
        }

        // Wallet is ready! Reset timeout tracker
        walletWaitStartTime.current = null;
        console.log("[Auth] Wallet ready:", walletAddress);

        // Add breadcrumb for auth flow tracking
        Sentry.addBreadcrumb({
          category: 'auth',
          message: 'Wallet ready, starting backend sync',
          level: 'info',
          data: { walletAddress },
        });

        // Mark as attempted BEFORE the call to prevent retry loops
        hasSyncedWithBackend.current = true;

        try {
          // Get Privy access token
          const privyToken = await getAccessToken();

          if (!privyToken) {
            setIsLoading(false);
            return;
          }

          // Try to get Farcaster FID (if running inside Farcaster client)
          let fid: number | undefined;
          try {
            const farcasterContext = await sdk.context;
            fid = farcasterContext?.user?.fid;
            if (fid) {
              console.log("[Auth] Farcaster FID detected:", fid);
            }
          } catch {
            // Not in Farcaster environment - this is expected for web/mobile
          }

          // Get user's timezone for date calculations
          const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

          // Call backend to verify and sync user
          const response = await authApi.loginWithPrivy(privyToken, fid, timezone);

          // Store Befit JWT and verify it's readable (iOS Safari fix)
          const tokenStored = storeAndVerifyToken(response.token);
          if (!tokenStored) {
            console.warn("[Auth] Token storage verification failed, retrying...");
            // Retry once after a small delay
            await new Promise(resolve => setTimeout(resolve, 50));
            storeAndVerifyToken(response.token);
          }

          // Mark token as ready BEFORE setting user (prevents race condition)
          setIsTokenReady(true);

          // Update local user state (this triggers dependent hooks)
          setUser(response.user);

          // Set Sentry user context for error tracking
          setSentryUser({
            id: response.user.id,
            email: response.user.email,
            walletAddress: response.user.walletAddress || undefined,
          });

          // Identify user in PostHog for analytics
          posthog.identify(response.user.id, {
            email: response.user.email,
            username: response.user.username,
            walletAddress: response.user.walletAddress,
          });

          // Track login completion
          analytics.track.loginCompleted(
            privyUser?.email?.address ? 'email' : 'google'
          );

          // Add breadcrumb for successful auth
          Sentry.addBreadcrumb({
            category: 'auth',
            message: 'User authenticated successfully',
            level: 'info',
            data: { userId: response.user.id, needsProfileCreation: response.needsProfileCreation },
          });

          // Navigate based on profile completion
          // ALWAYS redirect if profile is incomplete (username not set)
          // For completed profiles, only redirect on fresh login (to allow direct URL access)
          if (response.needsProfileCreation) {
            // Profile incomplete - always redirect regardless of fresh login or session restore
            if (!hasRedirectedToProfile) {
              setHasRedirectedToProfile(true);
              isFreshLogin.current = false;
              navigate("/profile-creation");
            }
          } else if (!hasRedirectedToProfile && isFreshLogin.current) {
            // Profile complete + fresh login - redirect to home
            setHasRedirectedToProfile(true);
            isFreshLogin.current = false;
            navigate("/");
          }
        } catch (error: any) {
          // Track auth sync error in Sentry
          Sentry.captureException(error, {
            tags: { auth_flow: 'backend_sync' },
            extra: {
              privyUserId: privyUser?.id,
              email: privyUser?.email?.address,
              walletAddress,
              errorMessage: error?.message,
              errorCode: error?.response?.data?.code,
            },
          });

          // Backend is required - force full logout
          await privyLogout();

          // Reset all state
          setIsTokenReady(false);
          setUser(null);
          hasSyncedWithBackend.current = false;
          setHasRedirectedToProfile(false);
          localStorage.removeItem("authToken");

          // Show user-friendly error modal
          setErrorTitle(getErrorTitle(error));
          setErrorMessage(refineErrorMessage(error));
          setShowErrorModal(true);
        }
      } else if (!authenticated) {
        // Reset all state when user logs out
        setIsTokenReady(false);
        setUser(null);
        setHasRedirectedToProfile(false);
        hasSyncedWithBackend.current = false;
        walletWaitStartTime.current = null;
        localStorage.removeItem("authToken");
        clearSentryUser();
      }
      setIsLoading(false);
    };

    if (ready) {
      syncUser();
    }
  }, [authenticated, ready, privyUser]); // privyUser triggers re-run when wallet is created

  const handleLogin = () => {
    analytics.track.loginStarted();
    isFreshLogin.current = true; // Mark as fresh login (not session restoration)
    privyLogin();
  };

  const handleLogout = async () => {
    try {
      await authApi.logout();
    } catch (error) {
      console.error("Logout error:", error);
      Sentry.captureException(error, {
        tags: { auth_flow: 'logout' },
      });
    } finally {
      setIsTokenReady(false);
      localStorage.removeItem("authToken");
      setUser(null);
      setHasRedirectedToProfile(false);
      hasSyncedWithBackend.current = false;
      isFreshLogin.current = false;
      walletWaitStartTime.current = null;
      clearSentryUser();
      analytics.track.logout(); // Track logout event
      posthog.reset(); // Clear PostHog user identification
      privyLogout();
    }
  };

  const updateUser = (updatedUser: User) => {
    setUser(updatedUser);
  };

  const value: AuthContextType = {
    user,
    privyUser,
    isLoading: !ready || isLoading,
    // User is authenticated if we have user data (from Privy sync OR JWT restore)
    isAuthenticated: !!user,
    // Token is ready when we've confirmed the auth token is stored and readable
    isTokenReady,
    login: handleLogin,
    logout: handleLogout,
    updateUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}

      {/* Authentication Error Modal */}
      <ErrorModal
        isOpen={showErrorModal}
        onClose={() => setShowErrorModal(false)}
        title={errorTitle}
        message={errorMessage}
        variant="error"
        actionLabel="Try Again"
      />
    </AuthContext.Provider>
  );
}

// Main Auth Provider with Privy + Alchemy wrapper
export function AuthProvider({ children }: { children: React.ReactNode }) {
  if (!PRIVY_APP_ID) {
    console.warn(
      "Privy App ID not configured. Please set VITE_PRIVY_APP_ID in .env"
    );
  }

  if (!ALCHEMY_API_KEY) {
    console.warn(
      "Alchemy API Key not configured. Please set VITE_ALCHEMY_API_KEY in .env"
    );
  }

  return (
    <>
      <AppUrlListener />
      <PrivyProvider
        appId={PRIVY_APP_ID}
        config={{
          loginMethods: ["email", "google"],
          embeddedWallets: {
            ethereum: {
              createOnLogin: "all-users",
            },
            showWalletUIs: false,
          },
          defaultChain: {
            id: 8453, // Base Mainnet
            name: "Base",
            network: "base-mainnet",
            nativeCurrency: {
              name: "Ethereum",
              symbol: "ETH",
              decimals: 18,
            },
            rpcUrls: {
              default: {
                http: [
                  `https://base-mainnet.g.alchemy.com/v2/${ALCHEMY_API_KEY}`,
                ],
              },
            },
            blockExplorers: {
              default: {
                name: "BaseScan",
                url: "https://basescan.org",
              },
            },
          },
          appearance: {
            theme: "dark",
            accentColor: "#10b981",
          },
          // customOAuthRedirectUrl: `${import.meta.env.VITE_NGROK_URL}/redirect`,
        }}
      >
        <AlchemyProvider
          apiKey={ALCHEMY_API_KEY}
          policyId={ALCHEMY_GAS_POLICY_ID}
          accountAuthMode="eip7702" // Use EIP-7702 for gasless transactions (default, recommended)
        >
          <AuthProviderInner>{children}</AuthProviderInner>
        </AlchemyProvider>
      </PrivyProvider>
    </>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
